import { CdkAccordionItem, CdkAccordion, CdkAccordionModule } from '@angular/cdk/accordion';
import { TemplatePortal, CdkPortalOutlet, PortalModule } from '@angular/cdk/portal';
import { DOCUMENT, AsyncPipe, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Directive, Inject, Optional, EventEmitter, booleanAttribute, Component, ViewEncapsulation, ChangeDetectionStrategy, SkipSelf, Input, Output, ContentChild, ViewChild, numberAttribute, Host, Attribute, HostListener, QueryList, ContentChildren, NgModule } from '@angular/core';
import { mixinVariant, SbbCommonModule } from '@sbb-esta/angular/core';
import * as i3 from '@sbb-esta/angular/icon';
import { SbbIconModule } from '@sbb-esta/angular/icon';
import * as i2 from '@angular/cdk/a11y';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { distinctUntilChanged, startWith, filter, take } from 'rxjs/operators';
import { ENTER, hasModifierKey, SPACE } from '@angular/cdk/keycodes';
import { Subject, Subscription, EMPTY, merge } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i1 from '@angular/cdk/collections';

/**
 * Token used to provide a `SbbAccordion` to `SbbExpansionPanel`.
 * Used primarily to avoid circular imports between `SbbAccordion` and `SbbExpansionPanel`.
 */
const SBB_ACCORDION = new InjectionToken('SBB_ACCORDION');

/** Time and timing curve for expansion panel animations. */
// Note: Keep this in sync with the Sass variable for the panel header animation.
const SBB_EXPANSION_PANEL_ANIMATION_TIMING = '225ms cubic-bezier(0.4,0.0,0.2,1)';
/**
 * Animations used by the Material expansion panel.
 *
 * A bug in angular animation's `state` when ViewContainers are moved using ViewContainerRef.move()
 * causes the animation state of moved components to become `void` upon exit, and not update again
 * upon reentry into the DOM.  This can lead a to situation for the expansion panel where the state
 * of the panel is `expanded` or `collapsed` but the animation state is `void`.
 *
 * To correctly handle animating to the next state, we animate between `void` and `collapsed` which
 * are defined to have the same styles. Since angular animates from the current styles to the
 * destination state's style definition, in situations where we are moving from `void`'s styles to
 * `collapsed` this acts a noop since no style values change.
 *
 * In the case where angular's animation state is out of sync with the expansion panel's state, the
 * expansion panel being `expanded` and angular animations being `void`, the animation from the
 * `expanded`'s effective styles (though in a `void` animation state) to the collapsed state will
 * occur as expected.
 *
 * Angular Bug: https://github.com/angular/angular/issues/18847
 *
 * @docs-private
 */
const sbbExpansionAnimations = {
    /** Animation that rotates the indicator arrow. */
    indicatorRotate: trigger('indicatorRotate', [
        state('collapsed, void', style({ transform: 'rotate(90deg)' })),
        state('expanded', style({ transform: 'rotate(-90deg)' })),
        transition('expanded <=> collapsed, void => collapsed', animate(SBB_EXPANSION_PANEL_ANIMATION_TIMING)),
    ]),
    /** Animation that expands and collapses the panel content. */
    bodyExpansion: trigger('bodyExpansion', [
        state('collapsed, void', style({ height: '0px', visibility: 'hidden' })),
        // Clear the `visibility` while open, otherwise the content will be visible when placed in
        // a parent that's `visibility: hidden`, because `visibility` doesn't apply to descendants
        // that have a `visibility` of their own (see #27436).
        state('expanded', style({ height: '*', visibility: '' })),
        transition('expanded <=> collapsed, void => collapsed', animate(SBB_EXPANSION_PANEL_ANIMATION_TIMING)),
    ]),
};

/**
 * Token used to provide a `SbbExpansionPanel` to `SbbExpansionPanelContent`.
 * Used to avoid circular imports between `SbbExpansionPanel` and `SbbExpansionPanelContent`.
 */
const SBB_EXPANSION_PANEL = new InjectionToken('SBB_EXPANSION_PANEL');

/**
 * Expansion panel content that will be rendered lazily
 * after the panel is opened for the first time.
 */
class SbbExpansionPanelContent {
    constructor(_template, _expansionPanel) {
        this._template = _template;
        this._expansionPanel = _expansionPanel;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbExpansionPanelContent, deps: [{ token: i0.TemplateRef }, { token: SBB_EXPANSION_PANEL, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbExpansionPanelContent, isStandalone: true, selector: "ng-template[sbbExpansionPanelContent]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbExpansionPanelContent, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ng-template[sbbExpansionPanelContent]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SBB_EXPANSION_PANEL]
                }, {
                    type: Optional
                }] }] });

// Boilerplate for applying mixins to SbbExpansionPanel.
/** @docs-private */
// tslint:disable-next-line: naming-convention
const _SbbExpansionPanelBase = mixinVariant(CdkAccordionItem);
/** Counter for generating unique element ids. */
let uniqueId = 0;
/**
 * This component can be used as a single element to show expandable content, or as one of
 * multiple children of an element with the SbbAccordion component attached.
 */
class SbbExpansionPanel extends _SbbExpansionPanelBase {
    /** Whether the toggle indicator should be hidden. */
    get hideToggle() {
        return this._hideToggle || (this.accordion && this.accordion.hideToggle);
    }
    set hideToggle(value) {
        this._hideToggle = value;
    }
    constructor(_viewContainerRef, accordion, changeDetectorRef, uniqueSelectionDispatcher, document) {
        super(accordion, changeDetectorRef, uniqueSelectionDispatcher);
        this._viewContainerRef = _viewContainerRef;
        this._hideToggle = false;
        /** An event emitted after the body's expansion animation happens. */
        this.afterExpand = new EventEmitter();
        /** An event emitted after the body's collapse animation happens. */
        this.afterCollapse = new EventEmitter();
        /** Stream that emits for changes in `@Input` properties. */
        this._inputChanges = new Subject();
        /** ID for the associated header element. Used for a11y labelling. */
        this._headerId = `sbb-expansion-panel-header-${uniqueId++}`;
        /** Stream of body animation done events. */
        this._bodyAnimationDone = new Subject();
        this.accordion = accordion;
        this._document = document;
        // We need a Subject with distinctUntilChanged, because the `done` event
        // fires twice on some browsers. See https://github.com/angular/angular/issues/24084
        this._bodyAnimationDone
            .pipe(distinctUntilChanged((x, y) => {
            return x.fromState === y.fromState && x.toState === y.toState;
        }))
            .subscribe((event) => {
            if (event.fromState !== 'void') {
                if (event.toState === 'expanded') {
                    this.afterExpand.emit();
                }
                else if (event.toState === 'collapsed') {
                    this.afterCollapse.emit();
                }
            }
        });
    }
    /** Gets the expanded state string. */
    _getExpandedState() {
        return this.expanded ? 'expanded' : 'collapsed';
    }
    /** Toggles the expanded state of the expansion panel. */
    toggle() {
        this.expanded = !this.expanded;
    }
    /** Sets the expanded state of the expansion panel to false. */
    close() {
        this.expanded = false;
    }
    /** Sets the expanded state of the expansion panel to true. */
    open() {
        this.expanded = true;
    }
    ngAfterContentInit() {
        if (this._lazyContent && this._lazyContent._expansionPanel === this) {
            // Render the content as soon as the panel becomes open.
            this.opened
                .pipe(startWith(null), filter(() => this.expanded && !this._portal), take(1))
                .subscribe(() => {
                this._portal = new TemplatePortal(this._lazyContent._template, this._viewContainerRef);
            });
        }
    }
    ngOnChanges(changes) {
        this._inputChanges.next(changes);
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this._bodyAnimationDone.complete();
        this._inputChanges.complete();
    }
    /** Checks whether the expansion panel's content contains the currently-focused element. */
    _containsFocus() {
        if (this._body) {
            const focusedElement = this._document.activeElement;
            const bodyElement = this._body.nativeElement;
            return focusedElement === bodyElement || bodyElement.contains(focusedElement);
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbExpansionPanel, deps: [{ token: i0.ViewContainerRef }, { token: SBB_ACCORDION, optional: true, skipSelf: true }, { token: i0.ChangeDetectorRef }, { token: i1.UniqueSelectionDispatcher }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "17.3.7", type: SbbExpansionPanel, isStandalone: true, selector: "sbb-expansion-panel", inputs: { expanded: ["expanded", "expanded", booleanAttribute], disabled: ["disabled", "disabled", booleanAttribute], hideToggle: ["hideToggle", "hideToggle", booleanAttribute] }, outputs: { afterExpand: "afterExpand", afterCollapse: "afterCollapse" }, host: { properties: { "class.sbb-expanded": "expanded" }, classAttribute: "sbb-expansion-panel" }, providers: [
            {
                provide: SBB_ACCORDION,
                useValue: undefined,
            },
            { provide: SBB_EXPANSION_PANEL, useExisting: SbbExpansionPanel },
        ], queries: [{ propertyName: "_lazyContent", first: true, predicate: SbbExpansionPanelContent, descendants: true }], viewQueries: [{ propertyName: "_body", first: true, predicate: ["body"], descendants: true }], exportAs: ["sbbExpansionPanel"], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<ng-content select=\"sbb-expansion-panel-header\"></ng-content>\n<div\n  class=\"sbb-expansion-panel-content\"\n  role=\"region\"\n  [@bodyExpansion]=\"_getExpandedState()\"\n  (@bodyExpansion.done)=\"_bodyAnimationDone.next($event)\"\n  [attr.aria-labelledby]=\"_headerId\"\n  [id]=\"id\"\n  #body\n>\n  <div class=\"sbb-expansion-panel-body sbb-expansion-panel-horizontal-padding\">\n    <ng-content></ng-content>\n    <ng-template [cdkPortalOutlet]=\"_portal\"></ng-template>\n  </div>\n</div>\n", styles: [".sbb-expansion-panel{box-sizing:content-box;display:block;margin:0;transition:border-color var(--sbb-transition-fast-start);border:var(--sbb-border-width-thin) solid var(--sbb-expansion-panel-border-color-closed)}.sbb-expansion-panel+.sbb-expansion-panel{margin-top:calc(var(--sbb-border-width-thin)*2)}.sbb-expansion-panel.sbb-expanded{border-color:var(--sbb-expansion-panel-border-color-open)}.sbb-expansion-panel.sbb-expanded.sbb-expansion-panel-full-width{border-left-color:rgba(0,0,0,0);border-right-color:rgba(0,0,0,0);border-left-width:0;border-right-width:0;padding-left:var(--sbb-border-width-thin);padding-right:var(--sbb-border-width-thin)}.sbb-expansion-panel-horizontal-padding{padding-left:calc(var(--sbb-expansion-panel-padding-horizontal) - var(--sbb-border-width-thin));padding-right:calc(var(--sbb-expansion-panel-padding-horizontal) - var(--sbb-border-width-thin))}.sbb-expansion-panel-header{cursor:pointer;display:flex;align-items:center;position:relative;background-color:var(--sbb-expansion-panel-background-color-closed);outline:none;padding-top:var(--sbb-expansion-panel-header-padding-top);padding-bottom:var(--sbb-expansion-panel-header-padding-bottom)}:where(html.sbb-lean) .sbb-expansion-panel-header{padding-bottom:calc(0.8rem - var(--sbb-border-width-thin))}.sbb-expansion-panel-header.sbb-expanded::after{content:\"\";display:block;position:absolute;height:0;bottom:0;width:calc(100% - var(--sbb-expansion-panel-padding-horizontal)*2 + var(--sbb-border-width-thin)*2);left:calc(var(--sbb-expansion-panel-padding-horizontal) - var(--sbb-border-width-thin));border-bottom:var(--sbb-border-width-thin) solid var(--sbb-expansion-panel-border-color-open)}.sbb-expansion-panel-header:is(.sbb-expanded,.sbb-expanded:is(:focus,:hover)){background:var(--sbb-expansion-panel-background-color-open)}.sbb-expansion-panel-header.sbb-disabled{cursor:default;opacity:.75}.sbb-expansion-panel-header-indicator{flex:0 0 auto;line-height:0;height:var(--sbb-icon-size-default);width:var(--sbb-icon-size-default);margin-left:1.6rem;color:var(--sbb-expansion-panel-header-indicator-color);margin-top:-0.5em;margin-bottom:-0.5em}:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{border:var(--sbb-border-width-thin) solid var(--sbb-color-storm);border-radius:50%;padding:calc(0.2666666667rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin))}@media all and (min-width: 40.1875em)and (max-width: 64.0525em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{margin-left:2.6666666667rem}}@media all and (min-width: 40.1875em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{height:calc(2.1333333333rem*var(--sbb-scaling-factor));width:calc(2.1333333333rem*var(--sbb-scaling-factor))}}@media all and (min-width: 160.0625em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{margin-left:2.4rem}}@media all and (min-width: 240.0625em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{margin-left:2.9333333333rem}}.sbb-expansion-panel-header-content{display:flex;flex:1;overflow:hidden;font-family:var(--sbb-font-light);font-size:var(--sbb-font-size-large);line-height:var(--sbb-expansion-panel-line-height)}.sbb-expansion-panel-content{display:flex;flex-direction:column;overflow:visible}.sbb-expansion-panel-content[style*=\"visibility: hidden\"] *{visibility:hidden !important}.sbb-expansion-panel-content.ng-animating,.sbb-expansion-panel:not(.sbb-expanded) .sbb-expansion-panel-content{overflow:hidden}.sbb-expansion-panel-body{font-size:var(--sbb-font-size);line-height:var(--sbb-line-height);padding-top:var(--sbb-divider-medium);padding-bottom:var(--sbb-divider-medium);background-color:var(--sbb-expansion-panel-background-color-open)}.sbb-expansion-panel-body>*:first-child{margin-top:0}"], dependencies: [{ kind: "directive", type: CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], animations: [sbbExpansionAnimations.bodyExpansion], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbExpansionPanel, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-expansion-panel', exportAs: 'sbbExpansionPanel', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, inputs: [
                        { name: 'expanded', transform: booleanAttribute },
                        { name: 'disabled', transform: booleanAttribute },
                    ], animations: [sbbExpansionAnimations.bodyExpansion], providers: [
                        {
                            provide: SBB_ACCORDION,
                            useValue: undefined,
                        },
                        { provide: SBB_EXPANSION_PANEL, useExisting: SbbExpansionPanel },
                    ], host: {
                        class: 'sbb-expansion-panel',
                        '[class.sbb-expanded]': 'expanded',
                    }, standalone: true, imports: [CdkPortalOutlet], template: "<ng-content select=\"sbb-expansion-panel-header\"></ng-content>\n<div\n  class=\"sbb-expansion-panel-content\"\n  role=\"region\"\n  [@bodyExpansion]=\"_getExpandedState()\"\n  (@bodyExpansion.done)=\"_bodyAnimationDone.next($event)\"\n  [attr.aria-labelledby]=\"_headerId\"\n  [id]=\"id\"\n  #body\n>\n  <div class=\"sbb-expansion-panel-body sbb-expansion-panel-horizontal-padding\">\n    <ng-content></ng-content>\n    <ng-template [cdkPortalOutlet]=\"_portal\"></ng-template>\n  </div>\n</div>\n", styles: [".sbb-expansion-panel{box-sizing:content-box;display:block;margin:0;transition:border-color var(--sbb-transition-fast-start);border:var(--sbb-border-width-thin) solid var(--sbb-expansion-panel-border-color-closed)}.sbb-expansion-panel+.sbb-expansion-panel{margin-top:calc(var(--sbb-border-width-thin)*2)}.sbb-expansion-panel.sbb-expanded{border-color:var(--sbb-expansion-panel-border-color-open)}.sbb-expansion-panel.sbb-expanded.sbb-expansion-panel-full-width{border-left-color:rgba(0,0,0,0);border-right-color:rgba(0,0,0,0);border-left-width:0;border-right-width:0;padding-left:var(--sbb-border-width-thin);padding-right:var(--sbb-border-width-thin)}.sbb-expansion-panel-horizontal-padding{padding-left:calc(var(--sbb-expansion-panel-padding-horizontal) - var(--sbb-border-width-thin));padding-right:calc(var(--sbb-expansion-panel-padding-horizontal) - var(--sbb-border-width-thin))}.sbb-expansion-panel-header{cursor:pointer;display:flex;align-items:center;position:relative;background-color:var(--sbb-expansion-panel-background-color-closed);outline:none;padding-top:var(--sbb-expansion-panel-header-padding-top);padding-bottom:var(--sbb-expansion-panel-header-padding-bottom)}:where(html.sbb-lean) .sbb-expansion-panel-header{padding-bottom:calc(0.8rem - var(--sbb-border-width-thin))}.sbb-expansion-panel-header.sbb-expanded::after{content:\"\";display:block;position:absolute;height:0;bottom:0;width:calc(100% - var(--sbb-expansion-panel-padding-horizontal)*2 + var(--sbb-border-width-thin)*2);left:calc(var(--sbb-expansion-panel-padding-horizontal) - var(--sbb-border-width-thin));border-bottom:var(--sbb-border-width-thin) solid var(--sbb-expansion-panel-border-color-open)}.sbb-expansion-panel-header:is(.sbb-expanded,.sbb-expanded:is(:focus,:hover)){background:var(--sbb-expansion-panel-background-color-open)}.sbb-expansion-panel-header.sbb-disabled{cursor:default;opacity:.75}.sbb-expansion-panel-header-indicator{flex:0 0 auto;line-height:0;height:var(--sbb-icon-size-default);width:var(--sbb-icon-size-default);margin-left:1.6rem;color:var(--sbb-expansion-panel-header-indicator-color);margin-top:-0.5em;margin-bottom:-0.5em}:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{border:var(--sbb-border-width-thin) solid var(--sbb-color-storm);border-radius:50%;padding:calc(0.2666666667rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin))}@media all and (min-width: 40.1875em)and (max-width: 64.0525em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{margin-left:2.6666666667rem}}@media all and (min-width: 40.1875em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{height:calc(2.1333333333rem*var(--sbb-scaling-factor));width:calc(2.1333333333rem*var(--sbb-scaling-factor))}}@media all and (min-width: 160.0625em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{margin-left:2.4rem}}@media all and (min-width: 240.0625em){:where(html:not(.sbb-lean)) .sbb-expansion-panel-header-indicator{margin-left:2.9333333333rem}}.sbb-expansion-panel-header-content{display:flex;flex:1;overflow:hidden;font-family:var(--sbb-font-light);font-size:var(--sbb-font-size-large);line-height:var(--sbb-expansion-panel-line-height)}.sbb-expansion-panel-content{display:flex;flex-direction:column;overflow:visible}.sbb-expansion-panel-content[style*=\"visibility: hidden\"] *{visibility:hidden !important}.sbb-expansion-panel-content.ng-animating,.sbb-expansion-panel:not(.sbb-expanded) .sbb-expansion-panel-content{overflow:hidden}.sbb-expansion-panel-body{font-size:var(--sbb-font-size);line-height:var(--sbb-line-height);padding-top:var(--sbb-divider-medium);padding-bottom:var(--sbb-divider-medium);background-color:var(--sbb-expansion-panel-background-color-open)}.sbb-expansion-panel-body>*:first-child{margin-top:0}"] }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }, {
                    type: Inject,
                    args: [SBB_ACCORDION]
                }] }, { type: i0.ChangeDetectorRef }, { type: i1.UniqueSelectionDispatcher }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { hideToggle: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], afterExpand: [{
                type: Output
            }], afterCollapse: [{
                type: Output
            }], _lazyContent: [{
                type: ContentChild,
                args: [SbbExpansionPanelContent]
            }], _body: [{
                type: ViewChild,
                args: ['body']
            }] } });

/**
 * This component corresponds to the header element of an `<sbb-expansion-panel>`.
 */
class SbbExpansionPanelHeader {
    constructor(panel, _element, _focusMonitor, _changeDetectorRef, _document, tabIndex) {
        this.panel = panel;
        this._element = _element;
        this._focusMonitor = _focusMonitor;
        this._changeDetectorRef = _changeDetectorRef;
        this._document = _document;
        this.tabIndex = 0;
        this._parentChangeSubscription = Subscription.EMPTY;
        const accordionHideToggleChange = panel.accordion
            ? panel.accordion._stateChanges.pipe(filter((changes) => !!changes.hideToggle))
            : EMPTY;
        this.tabIndex = parseInt(tabIndex || '', 10) || 0;
        // Since the toggle state depends on an @Input on the panel, we
        // need to subscribe and trigger change detection manually.
        this._parentChangeSubscription = merge(panel.opened, panel.closed, accordionHideToggleChange, panel._inputChanges.pipe(filter((changes) => !!(changes.hideToggle || changes.disabled)))).subscribe(() => this._changeDetectorRef.markForCheck());
        // Avoids focus being lost if the panel contained the focused element and was closed.
        panel.closed
            .pipe(filter(() => panel._containsFocus()))
            .subscribe(() => _focusMonitor.focusVia(_element, 'program'));
    }
    /**
     * Whether the associated panel is disabled. Implemented as a part of `FocusableOption`.
     * @docs-private
     */
    get disabled() {
        return this.panel.disabled;
    }
    /** Toggles the expanded state of the panel. */
    _toggle() {
        if (!this.disabled) {
            this.panel.toggle();
        }
    }
    /** Gets whether the panel is expanded. */
    _isExpanded() {
        return this.panel.expanded;
    }
    /** Gets the expanded state string of the panel. */
    _getExpandedState() {
        return this.panel._getExpandedState();
    }
    /** Gets the panel id. */
    _getPanelId() {
        return this.panel.id;
    }
    /** Gets whether the expand indicator should be shown. */
    _showToggle() {
        return !this.panel.hideToggle && !this.panel.disabled;
    }
    /** Handle keydown event calling to toggle() if appropriate. */
    _keydown(event) {
        switch (event.keyCode) {
            // Toggle for space and enter keys.
            case SPACE:
            case ENTER:
                if (!hasModifierKey(event) && this._isFocused()) {
                    // See https://github.com/sbb-design-systems/sbb-angular/issues/377
                    event.preventDefault();
                    this._toggle();
                }
                break;
            default:
                if (this.panel.accordion) {
                    this.panel.accordion._handleHeaderKeydown(event);
                }
                return;
        }
    }
    /**
     * Focuses the panel header. Implemented as a part of `FocusableOption`.
     * @param origin Origin of the action that triggered the focus.
     * @docs-private
     */
    focus(origin, options) {
        if (origin) {
            this._focusMonitor.focusVia(this._element, origin, options);
        }
        else {
            this._element.nativeElement.focus(options);
        }
    }
    ngAfterViewInit() {
        this._focusMonitor.monitor(this._element).subscribe((origin) => {
            if (origin && this.panel.accordion) {
                this.panel.accordion._handleHeaderFocus(this);
            }
        });
    }
    ngOnDestroy() {
        this._parentChangeSubscription.unsubscribe();
        this._focusMonitor.stopMonitoring(this._element);
    }
    _isFocused() {
        return this._document?.activeElement === this._element.nativeElement;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbExpansionPanelHeader, deps: [{ token: SbbExpansionPanel, host: true }, { token: i0.ElementRef }, { token: i2.FocusMonitor }, { token: i0.ChangeDetectorRef }, { token: DOCUMENT }, { token: 'tabindex', attribute: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbExpansionPanelHeader, isStandalone: true, selector: "sbb-expansion-panel-header", inputs: { tabIndex: ["tabIndex", "tabIndex", (value) => (value == null ? 0 : numberAttribute(value))] }, host: { attributes: { "role": "button" }, listeners: { "click": "_toggle()", "keydown": "_keydown($event)" }, properties: { "attr.id": "panel._headerId", "attr.tabindex": "disabled ? -1 : tabIndex", "attr.aria-controls": "_getPanelId()", "attr.aria-expanded": "_isExpanded()", "attr.aria-disabled": "disabled", "class.sbb-expanded": "_isExpanded()", "class.sbb-disabled": "disabled" }, classAttribute: "sbb-expansion-panel-header sbb-expansion-panel-horizontal-padding" }, ngImport: i0, template: "<span class=\"sbb-expansion-panel-header-content\">\n  <ng-content></ng-content>\n</span>\n@if (_showToggle()) {\n  <span class=\"sbb-expansion-panel-header-indicator sbb-icon-fit\">\n    @switch (panel.variant | async) {\n      @case ('standard') {\n        @switch (_isExpanded()) {\n          @case (false) {\n            <sbb-icon svgIcon=\"plus-small\"></sbb-icon>\n          }\n          @case (true) {\n            <sbb-icon svgIcon=\"minus-small\"></sbb-icon>\n          }\n        }\n      }\n      @default {\n        <sbb-icon [@indicatorRotate]=\"_getExpandedState()\" svgIcon=\"chevron-right-small\"></sbb-icon>\n      }\n    }\n  </span>\n}\n", dependencies: [{ kind: "ngmodule", type: SbbIconModule }, { kind: "component", type: i3.SbbIcon, selector: "sbb-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon", "id"], exportAs: ["sbbIcon"] }, { kind: "pipe", type: AsyncPipe, name: "async" }], animations: [sbbExpansionAnimations.indicatorRotate], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbExpansionPanelHeader, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-expansion-panel-header', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, inputs: ['tabIndex'], animations: [sbbExpansionAnimations.indicatorRotate], host: {
                        class: 'sbb-expansion-panel-header sbb-expansion-panel-horizontal-padding',
                        role: 'button',
                        '[attr.id]': 'panel._headerId',
                        '[attr.tabindex]': 'disabled ? -1 : tabIndex',
                        '[attr.aria-controls]': '_getPanelId()',
                        '[attr.aria-expanded]': '_isExpanded()',
                        '[attr.aria-disabled]': 'disabled',
                        '[class.sbb-expanded]': '_isExpanded()',
                        '[class.sbb-disabled]': 'disabled',
                    }, standalone: true, imports: [SbbIconModule, AsyncPipe], template: "<span class=\"sbb-expansion-panel-header-content\">\n  <ng-content></ng-content>\n</span>\n@if (_showToggle()) {\n  <span class=\"sbb-expansion-panel-header-indicator sbb-icon-fit\">\n    @switch (panel.variant | async) {\n      @case ('standard') {\n        @switch (_isExpanded()) {\n          @case (false) {\n            <sbb-icon svgIcon=\"plus-small\"></sbb-icon>\n          }\n          @case (true) {\n            <sbb-icon svgIcon=\"minus-small\"></sbb-icon>\n          }\n        }\n      }\n      @default {\n        <sbb-icon [@indicatorRotate]=\"_getExpandedState()\" svgIcon=\"chevron-right-small\"></sbb-icon>\n      }\n    }\n  </span>\n}\n" }]
        }], ctorParameters: () => [{ type: SbbExpansionPanel, decorators: [{
                    type: Host
                }] }, { type: i0.ElementRef }, { type: i2.FocusMonitor }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['tabindex']
                }] }], propDecorators: { tabIndex: [{
                type: Input,
                args: [{ transform: (value) => (value == null ? 0 : numberAttribute(value)) }]
            }], _toggle: [{
                type: HostListener,
                args: ['click']
            }], _keydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class SbbAccordion extends CdkAccordion {
    constructor() {
        super(...arguments);
        /** Headers belonging to this accordion. */
        this._ownHeaders = new QueryList();
        /** Whether the expansion indicator should be hidden. */
        this.hideToggle = false;
    }
    ngAfterContentInit() {
        this._headers.changes
            .pipe(startWith(this._headers))
            .subscribe((headers) => {
            this._ownHeaders.reset(headers.filter((header) => header.panel.accordion === this));
            this._ownHeaders.notifyOnChanges();
        });
        this._keyManager = new FocusKeyManager(this._ownHeaders).withWrap().withHomeAndEnd();
    }
    /** Handles keyboard events coming in from the panel headers. */
    _handleHeaderKeydown(event) {
        this._keyManager.onKeydown(event);
    }
    _handleHeaderFocus(header) {
        this._keyManager.updateActiveItem(header);
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this._keyManager?.destroy();
        this._ownHeaders.destroy();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbAccordion, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.7", type: SbbAccordion, isStandalone: true, selector: "sbb-accordion", inputs: { hideToggle: ["hideToggle", "hideToggle", booleanAttribute] }, host: { classAttribute: "sbb-accordion" }, providers: [
            {
                provide: SBB_ACCORDION,
                useExisting: SbbAccordion,
            },
        ], queries: [{ propertyName: "_headers", predicate: SbbExpansionPanelHeader, descendants: true }], exportAs: ["sbbAccordion"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbAccordion, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-accordion',
                    exportAs: 'sbbAccordion',
                    providers: [
                        {
                            provide: SBB_ACCORDION,
                            useExisting: SbbAccordion,
                        },
                    ],
                    host: {
                        class: 'sbb-accordion',
                    },
                    standalone: true,
                }]
        }], propDecorators: { _headers: [{
                type: ContentChildren,
                args: [SbbExpansionPanelHeader, { descendants: true }]
            }], hideToggle: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }] } });

class SbbAccordionModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbAccordionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbAccordionModule, imports: [CommonModule,
            CdkAccordionModule,
            PortalModule,
            SbbCommonModule,
            SbbIconModule,
            SbbAccordion,
            SbbExpansionPanel,
            SbbExpansionPanelHeader,
            SbbExpansionPanelContent], exports: [SbbAccordion, SbbExpansionPanel, SbbExpansionPanelHeader, SbbExpansionPanelContent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbAccordionModule, imports: [CommonModule,
            CdkAccordionModule,
            PortalModule,
            SbbCommonModule,
            SbbIconModule,
            SbbExpansionPanelHeader] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbAccordionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CdkAccordionModule,
                        PortalModule,
                        SbbCommonModule,
                        SbbIconModule,
                        SbbAccordion,
                        SbbExpansionPanel,
                        SbbExpansionPanelHeader,
                        SbbExpansionPanelContent,
                    ],
                    exports: [SbbAccordion, SbbExpansionPanel, SbbExpansionPanelHeader, SbbExpansionPanelContent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SBB_ACCORDION, SBB_EXPANSION_PANEL, SBB_EXPANSION_PANEL_ANIMATION_TIMING, SbbAccordion, SbbAccordionModule, SbbExpansionPanel, SbbExpansionPanelContent, SbbExpansionPanelHeader, sbbExpansionAnimations };
